import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { ReadingProgress } from "../components/ReadingProgress";
import { Glossary } from "../components/Glossary";
import Footer from "../components/Footer";
import { TeamsFrame } from "../components/TeamsFrame";
import { CayusWave } from "../components/CayusWave";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useRefonte } from "../translations/refonte";

const ProductPage = ({ slug }) => {
  const { language } = useLanguage();
  const product = useRefonte(language).product;
  const page = product[slug];
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  const slides = page.blocks.map((b) => ({ img: b.img, title: b.heading, text: b.caption }));

  const scrollToDemo = () => {
    const el = document.querySelector("#demo-form");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`product-page-${slug}`}>
      <ReadingProgress />
      <Navbar isScrolled={isScrolled} />
      <main className="pt-28"><Glossary>
        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-10">
          <p className="mono-cayus text-xs text-[#0047AB] mb-5">{page.eyebrow}</p>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#0A2342] tracking-tight mb-5">{page.title}</h1>
          <p className="font-body-cayus text-lg text-[#4D4D4D] leading-relaxed max-w-2xl mx-auto mb-8">{page.intro}</p>
          <button
            onClick={scrollToDemo}
            className="rounded-full px-7 py-3.5 bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F] transition-colors font-body-cayus inline-flex items-center gap-2"
            data-testid="product-hero-cta"
          >
            {page.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Promesses */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
          <div className="grid sm:grid-cols-3 gap-4" data-testid="product-promises">
            {page.promises.map((p, i) => (
              <div key={i} className="bg-[#F7F9FC] border border-[#C3CFE0] rounded-2xl px-5 py-4 flex gap-3">
                <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                <p className="font-body-cayus text-sm text-[#3A3F4B] leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>

        <CayusWave className="w-full h-8 mb-14" opacity={0.3} />

        {/* Blocs alternés : côte à côte, puis capture pleine largeur */}
        <div className="wrap-cayus flex flex-col gap-20 lg:gap-24 mb-20">
          {page.blocks.map((b, i) => {
            const wide = i % 2 === 1;
            const text = (
              <div className={wide ? "max-w-2xl mb-8" : ""}>
                <p className="mono-cayus text-inkc-300 mb-2">0{i + 1}</p>
                <h2 className="font-bold text-2xl sm:text-3xl text-inkc-800 tracking-[-0.025em] mb-4">{b.heading}</h2>
                <p className="text-lg text-inkc-600">{b.text}</p>
                {b.box && (
                  <div className="mt-5 border-l-[3px] border-[#10B981] bg-[#D1FAE5]/40 rounded-r-lg px-4 py-3">
                    <p className="text-sm text-inkc-700">{b.box}</p>
                  </div>
                )}
                {b.quote && <p className="pull-quote mt-5 text-xl text-cobalt-600">« {b.quote} »</p>}
              </div>
            );
            const frame = <TeamsFrame src={b.img} alt={b.caption} caption={b.caption} slides={slides} slideIndex={i} />;
            return wide ? (
              <div key={i} className="max-w-[1120px]">
                {text}
                {frame}
              </div>
            ) : (
              <div key={i} className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-14 items-center">
                {frame}
                {text}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="ink-section py-14 text-center">
          <p className="font-display font-semibold text-2xl sm:text-3xl text-white mb-6 px-4">{product.ctaSection}</p>
          <button
            onClick={scrollToDemo}
            className="rounded-full px-8 py-4 bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F] transition-colors font-body-cayus inline-flex items-center gap-2"
            data-testid="product-cta-demo"
          >
            {page.cta}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </Glossary></main>
      <Footer />
    </div>
  );
};

export default ProductPage;
